import { BrowserRouter, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { RefreshCw, Copy, Check, Settings as SettingsIcon, ArrowLeft,
  Plus, Minus, Pencil, ExternalLink, Clock, Mail, PlayCircle, ClipboardList,
  Upload, AlertTriangle, History, RotateCcw,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Interceptor: inietta il PIN salvato in ogni richiesta
axios.interceptors.request.use((config) => {
  const pin = localStorage.getItem("app_pin");
  if (pin) config.headers["X-App-Pin"] = pin;
  return config;
});
// Su 401 → forza re-login
axios.interceptors.response.use((r) => r, (err) => {
  if (err?.response?.status === 401) {
    localStorage.removeItem("app_pin");
    if (window.location.pathname !== "/login") window.location.href = "/login";
  }
  return Promise.reject(err);
});

function PinGate() {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/verify`, { pin });
      if (r.data.ok) {
        localStorage.setItem("app_pin", pin);
        window.location.href = "/";
      }
    } catch {
      toast.error("PIN non valido");
    }
    setLoading(false);
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-8 space-y-5">
        <div className="text-center">
          <div className="w-12 h-12 rounded-md bg-slate-900 text-white grid place-items-center font-black mx-auto mb-4">IC</div>
          <h1 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Accesso protetto</h1>
          <p className="text-xs text-slate-500 mt-1">Inserisci il PIN per accedere alla dashboard</p>
        </div>
        <Input
          type="password"
          inputMode="numeric"
          autoFocus
          placeholder="PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          className="text-center text-2xl tracking-widest font-mono"
          data-testid="pin-input"
        />
        <Button type="submit" disabled={loading || !pin} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="pin-submit">
          {loading ? "Verifica…" : "Entra"}
        </Button>
      </form>
      <Toaster position="top-right" />
    </div>
  );
}

function RequireAuth({ children }) {
  const has = !!localStorage.getItem("app_pin");
  if (!has) return <PinGate />;
  return children;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <div className="w-9 h-9 rounded-md bg-slate-900 text-white grid place-items-center font-black">IC</div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                ItaliaCorsi · Sync Console
              </div>
              <div className="text-[11px] text-slate-500 uppercase tracking-[0.15em]">Corsi p-learning → italiacorsi.it</div>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/settings" data-testid="nav-settings-link">
              <Button variant="outline" size="sm" className="gap-2"><SettingsIcon className="w-4 h-4" />Impostazioni</Button>
            </Link>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">{children}</main>
      <Toaster position="top-right" />
    </div>
  );
}

function Dashboard() {
  const [subs, setSubs] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/subscriptions`),
        axios.get(`${API}/sync/status`),
      ]);
      setSubs(a.data);
      setStatus(b.data);
    } catch (e) { toast.error("Errore caricamento dati"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Polling stato sync durante l'esecuzione
  useEffect(() => {
    if (!syncing) return;
    let stop = false;
    const poll = async () => {
      while (!stop) {
        try {
          const r = await axios.get(`${API}/sync/current`);
          setProgress(r.data);
          if (!r.data.running) {
            // Completato
            setSyncing(false);
            const res = r.data.results || {};
            const errors = Object.values(res).filter(x => x.status === "error").length;
            if (errors > 0) toast.error(`Sync completato con ${errors} errori`);
            else toast.success("Sync completato con successo!");
            await load();
            return;
          }
        } catch { /* ignore */ }
        await new Promise(res => setTimeout(res, 1500));
      }
    };
    poll();
    return () => { stop = true; };
  }, [syncing]);

  const runSync = async () => {
    setSyncing(true);
    setProgress({ running: true, step: "Avvio...", progress: 0, total: 6 });
    try {
      await axios.post(`${API}/sync/run`);
      toast.info("Sincronizzazione avviata. Attendere ~50 secondi…");
    } catch {
      toast.error("Errore avvio sync");
      setSyncing(false);
    }
  };

  const nextRun = status?.jobs?.[0]?.next_run;
  const pct = progress && progress.total > 0 ? Math.round((progress.progress / progress.total) * 100) : 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Dashboard Sincronizzazione
          </h1>
          <p className="text-sm text-slate-600 mt-1">Monitora i 3 abbonamenti p-learning e aggiorna le tabelle su italiacorsi.it.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 bg-white">
            <Clock className="w-3.5 h-3.5" />
            {nextRun
              ? <>Prossimo sync: <span className="font-mono font-semibold text-slate-800" data-testid="next-run">{fmtDate(nextRun)}</span></>
              : <span className="font-semibold text-slate-800" data-testid="next-run">Sync manuale (auto disattivato)</span>}
          </div>
          <Button onClick={runSync} disabled={syncing} className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="run-sync-now-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sync in corso…" : "Esegui sync ora"}
          </Button>
        </div>
      </div>

      {syncing && progress && (
        <div className="border border-slate-300 bg-white rounded-md p-4 shadow-sm" data-testid="sync-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <div className="flex items-center gap-2 text-slate-800">
              <RefreshCw className="w-4 h-4 animate-spin text-slate-600" />
              <span className="font-semibold">Sincronizzazione in corso</span>
              <span className="text-slate-500">— {progress.step || "…"}</span>
            </div>
            <div className="font-mono text-xs text-slate-600">
              {progress.progress}/{progress.total} · {pct}%
            </div>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-slate-900 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Ogni pagina viene scaricata e analizzata. Durata tipica: 45-60 secondi totali.</p>
        </div>
      )}

      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {subs.map((s) => (
          <Card key={s.key} className="border-slate-200 hover:shadow-sm transition-shadow" data-testid={`subscription-card-${s.key}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base font-semibold leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {s.name}
                </CardTitle>
                {s.last_status === "error" ? (
                  <Badge variant="destructive" className="text-[10px]">Errore</Badge>
                ) : s.has_pending ? (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Da aggiornare</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">Allineato</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-center pb-2 border-b border-slate-100">
                <div>
                  <div className="text-xl font-black text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.plearning_courses}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Su p-learning</div>
                </div>
                <div>
                  <div className="text-xl font-black text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.italia_courses}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Su italiacorsi</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-2xl font-black text-emerald-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>+{s.added}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Da aggiungere</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-red-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>−{s.removed}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Da rimuovere</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-amber-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>~{s.modified}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Da correggere</div>
                </div>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <Clock className="w-3 h-3" /> Ultimo sync: <span className="font-mono text-slate-700">{fmtDate(s.last_sync)}</span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <Link to={`/subscription/${s.key}`} className="flex-1" data-testid={`open-${s.key}-btn`}>
                  <Button variant="default" className="w-full bg-slate-900 hover:bg-slate-800 gap-2">
                    Vedi differenze
                  </Button>
                </Link>
                <a href={s.target_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="icon" title="Apri pagina italiacorsi.it"><ExternalLink className="w-4 h-4" /></Button>
                </a>
              </div>
            </CardContent>
          </Card>
        ))}
        {subs.length === 0 && !loading && (
          <div className="col-span-full text-center text-slate-500 py-12 border border-dashed border-slate-300 rounded-md">
            Nessun dato ancora. Clicca <b>Esegui sync ora</b> per fare il primo scraping.
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <ClipboardList className="w-4 h-4" /> Cronologia sync
        </h2>
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Eseguito il</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Premium</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">CTI Premium</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Premium + CTI</th>
              </tr>
            </thead>
            <tbody>
              {(status?.recent_runs || []).map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2 font-mono text-slate-700 text-xs">{fmtDate(r.ran_at)}</td>
                  {["premium", "cti-premium", "premium-cti"].map((k) => {
                    const res = r.results?.[k];
                    return (
                      <td key={k} className="px-4 py-2 text-xs">
                        {res?.status === "success"
                          ? <span className="text-emerald-700 font-semibold">✓ {res.count} corsi</span>
                          : <span className="text-red-700">✗ {res?.error?.slice(0, 40) || "errore"}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {(!status?.recent_runs || status.recent_runs.length === 0) && (
                <tr><td colSpan={4} className="text-center py-6 text-slate-500 text-sm">Nessuna esecuzione registrata.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function CourseRow({ course, kind = "added" }) {
  const cfp = course.cfp || {};
  const bg = kind === "added" ? "bg-emerald-50" : kind === "removed" ? "bg-red-50" : "bg-amber-50";
  const icon = kind === "added" ? <Plus className="w-3 h-3 text-emerald-700" />
    : kind === "removed" ? <Minus className="w-3 h-3 text-red-700" />
    : <Pencil className="w-3 h-3 text-amber-700" />;
  return (
    <tr className={`${bg} border-b border-slate-100`}>
      <td className="px-3 py-2">{icon}</td>
      <td className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">{course.category}</td>
      <td className="px-3 py-2 text-sm text-slate-800">{course.title}</td>
      <td className="px-3 py-2 text-center font-mono text-xs">{course.hours || "-"}</td>
      <td className="px-3 py-2 text-center font-mono text-xs">{cfp.CNPI || "-"}</td>
      <td className="px-3 py-2 text-center font-mono text-xs">{cfp.CNAPPC || "-"}</td>
      <td className="px-3 py-2 text-center font-mono text-xs">{cfp.CNI || "-"}</td>
      <td className="px-3 py-2 text-center font-mono text-xs">{cfp.CNG || "-"}</td>
      <td className="px-3 py-2 text-center font-mono text-xs">{cfp.Geologi || "-"}</td>
    </tr>
  );
}

function Detail() {
  const { key } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pagePreview, setPagePreview] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  const openPublish = async () => {
    setPublishOpen(true);
    setLoadingPreview(true);
    setPagePreview("");
    try {
      const r = await axios.get(`${API}/subscriptions/${key}/preview-page`);
      setPagePreview(r.data.html || "");
    } catch (e) {
      // Fallback: se full-page non disponibile (503 in prod senza Playwright),
      // costruisco un'anteprima HTML self-contained con la sola tabella
      try {
        const h = await axios.get(`${API}/subscriptions/${key}/html`);
        const fallbackHtml = `
          <!doctype html><html><head><meta charset="utf-8">
            <base href="https://www.italiacorsi.it/">
            <title>Anteprima tabella</title>
            <link rel="stylesheet" href="https://www.italiacorsi.it/wp-content/themes/italiacorsi/style.css">
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css">
            <style>
              body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; margin: 0; background: #fff; color:#222; }
              .banner { background: #F59E0B; color: #000; padding: 10px 14px; border-radius: 6px; margin-bottom: 20px; font-weight: 600; font-size: 13px; text-align: center; }
              .info { background:#EEF2FF; border-left:4px solid #4F46E5; padding:12px 14px; margin-bottom:20px; font-size:13px; color:#1E3A8A; border-radius:4px; }
              table.corsi { width: 100%; }
              .note { color: #64748b; font-size: 12px; margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
            </style>
          </head><body>
            <div class="banner">Anteprima solo tabella - la vista full-page non e' disponibile in produzione</div>
            <div class="info">
              <b>Nessun rischio estetico:</b> la tabella qui sotto ha stili inline (colori, bordi, larghezze). Quando la pubblichi su italiacorsi.it viene inserita nella descrizione del prodotto e il resto della pagina (header, menu, prezzo, footer) resta INVARIATO. Il risultato finale sara' identico graficamente. In piu' e' sempre attivo il backup automatico con rollback in un click se qualcosa non va.
            </div>
            ${h.data.html}
            <p class="note">Il contenuto pubblicato conserva il layout del sito perche' viene iniettato solo nella descrizione prodotto WooCommerce.</p>
          </body></html>
        `;
        setPagePreview(fallbackHtml);
      } catch (e2) {
        toast.error(`Errore anteprima: ${e2?.response?.data?.detail || e.message}`);
      }
    }
    setLoadingPreview(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        axios.get(`${API}/subscriptions/${key}`),
        axios.get(`${API}/subscriptions/${key}/html`),
      ]);
      setData(r.data);
      setPreviewHtml(h.data.html || "");
    } catch { toast.error("Errore caricamento dettaglio"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [key]);

  const approve = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/subscriptions/${key}/refresh-italia`);
      toast.success("Pagina italiacorsi ri-scansionata. Diff aggiornato.");
      load();
    } catch { toast.error("Errore aggiornamento"); }
    setApproving(false);
  };

  const copyHTML = async () => {
    try {
      const r = await axios.get(`${API}/subscriptions/${key}/html`);
      await navigator.clipboard.writeText(r.data.html);
      toast.success(`HTML tabella copiato (${r.data.count} corsi). Incollalo nella pagina prodotto WordPress.`);
    } catch { toast.error("Errore generazione HTML"); }
  };

  const [backups, setBackups] = useState([]);
  const [restoring, setRestoring] = useState("");

  const loadBackups = async () => {
    try {
      const r = await axios.get(`${API}/subscriptions/${key}/backups`);
      setBackups(r.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadBackups(); }, [key]);

  const publish = async () => {
    setPublishing(true);
    try {
      const r = await axios.post(`${API}/subscriptions/${key}/publish`);
      toast.success(`Pubblicato! ${r.data.courses_count} corsi aggiornati (prodotto #${r.data.product_id}). Backup salvato.`);
      setPublishOpen(false);
      load();
      loadBackups();
    } catch (e) {
      toast.error(`Errore pubblicazione: ${e?.response?.data?.detail || e.message}`);
    }
    setPublishing(false);
  };

  const restore = async (backupId) => {
    if (!window.confirm("Ripristinare la versione precedente della pagina italiacorsi? La versione attuale verrà salvata come safety-backup.")) return;
    setRestoring(backupId);
    try {
      await axios.post(`${API}/subscriptions/${key}/restore/${backupId}`);
      toast.success("Ripristino completato.");
      load();
      loadBackups();
    } catch (e) {
      toast.error(`Errore ripristino: ${e?.response?.data?.detail || e.message}`);
    }
    setRestoring("");
  };

  if (loading || !data) return <div className="text-slate-500">Caricamento…</div>;
  const { info, diff, latest } = data;
  const courses = latest?.courses || [];
  const byCat = courses.reduce((acc, c) => { (acc[c.category] = acc[c.category] || []).push(c); return acc; }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => nav("/")} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
        </button>
        <a href={info.target_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Pagina italiacorsi.it
        </a>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{info.key}</div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>{info.name}</h1>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
            <Clock className="w-3 h-3" /> Ultimo scraping: <span className="font-mono">{fmtDate(latest?.scraped_at)}</span>
            · Corsi rilevati: <b className="text-slate-800">{latest?.total_count || 0}</b>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={copyHTML} className="gap-2" data-testid="copy-html-btn">
            <Copy className="w-4 h-4" /> Copia HTML
          </Button>
          <Button variant="outline" onClick={approve} disabled={approving} className="gap-2" data-testid="approve-snapshot-btn">
            <RefreshCw className={`w-4 h-4 ${approving ? "animate-spin" : ""}`} /> {approving ? "Ri-scansiono…" : "Ricontrolla italiacorsi"}
          </Button>
          <Button onClick={openPublish} className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="publish-btn">
            <Upload className="w-4 h-4" /> Pubblica su italiacorsi
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" title="Cronologia backup / Ripristino" data-testid="backups-menu-btn">
                <History className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="text-xs">Backup automatici ({backups.length})</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {backups.length === 0 && (
                <div className="px-2 py-4 text-xs text-slate-500 text-center">Nessun backup ancora.<br />Ogni pubblicazione crea un backup automatico.</div>
              )}
              {backups.slice(0, 10).map((bk) => (
                <DropdownMenuItem
                  key={bk.id}
                  className="flex flex-col items-start gap-0.5 cursor-pointer"
                  onSelect={(e) => { e.preventDefault(); restore(bk.id); }}
                  data-testid={`restore-${bk.id}`}
                >
                  <div className="flex items-center gap-2 text-xs w-full">
                    <RotateCcw className={`w-3 h-3 ${restoring === bk.id ? "animate-spin" : ""}`} />
                    <span className="font-mono">{fmtDate(bk.created_at)}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 pl-5">
                    {bk.kind === "pre-restore-backup" ? "Safety (pre-restore)" : "Pre-publish"} · {(bk.html_length/1024).toFixed(1)}KB
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Tabs defaultValue="diff" className="w-full">
        <TabsList>
          <TabsTrigger value="diff" data-testid="tab-diff">
            Vista Diff
            <span className="ml-2 flex gap-1">
              {diff.added.length > 0 && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">+{diff.added.length}</Badge>}
              {diff.removed.length > 0 && <Badge className="bg-red-100 text-red-800 hover:bg-red-100">−{diff.removed.length}</Badge>}
              {diff.modified.length > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">~{diff.modified.length}</Badge>}
            </span>
          </TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview">Anteprima HTML</TabsTrigger>
          <TabsTrigger value="full" data-testid="tab-full">Tabella completa</TabsTrigger>
        </TabsList>

        <TabsContent value="diff" className="mt-4">
          {(diff.added.length + diff.removed.length + diff.modified.length) === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-slate-500">
              La tua pagina italiacorsi.it è perfettamente allineata con p-learning. Nessuna azione richiesta ✓
            </div>
          ) : (
            <div className="space-y-6">
              {diff.added.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2 text-emerald-800">
                      <Plus className="w-4 h-4" /> Da aggiungere su italiacorsi.it ({diff.added.length})
                    </h3>
                    <span className="text-xs text-slate-500">Presenti su p-learning ma non nella tua tabella</span>
                  </div>
                  <div className="border border-emerald-200 rounded-md bg-white overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-emerald-50 border-b border-emerald-200">
                        <tr className="text-xs uppercase tracking-wider text-emerald-800">
                          <th className="text-left px-3 py-2">Categoria</th>
                          <th className="text-left px-3 py-2">Titolo</th>
                          <th className="px-3 py-2 text-center">Ore</th>
                          <th className="px-3 py-2 text-center">CNPI</th>
                          <th className="px-3 py-2 text-center">CNAPPC</th>
                          <th className="px-3 py-2 text-center">CNI</th>
                          <th className="px-3 py-2 text-center">CNG</th>
                          <th className="px-3 py-2 text-center">Geologi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diff.added.map((c, i) => (
                          <tr key={`a${i}`} className="border-b border-slate-100 hover:bg-emerald-50/40">
                            <td className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">{c.category}</td>
                            <td className="px-3 py-2 text-slate-900">{c.title}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.hours || "-"}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNPI || "-"}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNAPPC || "-"}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNI || "-"}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNG || "-"}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.Geologi || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {diff.removed.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2 text-red-800">
                      <Minus className="w-4 h-4" /> Da rimuovere da italiacorsi.it ({diff.removed.length})
                    </h3>
                    <span className="text-xs text-slate-500">Presenti nella tua tabella ma non più su p-learning</span>
                  </div>
                  <div className="border border-red-200 rounded-md bg-white overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 border-b border-red-200">
                        <tr className="text-xs uppercase tracking-wider text-red-800">
                          <th className="text-left px-3 py-2">Categoria</th>
                          <th className="text-left px-3 py-2">Titolo</th>
                          <th className="px-3 py-2 text-center">Ore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diff.removed.map((c, i) => (
                          <tr key={`r${i}`} className="border-b border-slate-100 hover:bg-red-50/40">
                            <td className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">{c.category}</td>
                            <td className="px-3 py-2 text-slate-900 line-through decoration-red-400">{c.title}</td>
                            <td className="px-3 py-2 text-center font-mono text-xs">{c.hours || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {diff.modified.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2 text-amber-800">
                      <Pencil className="w-4 h-4" /> Da correggere ({diff.modified.length})
                    </h3>
                    <span className="text-xs text-slate-500">Stesso corso ma ore o CFP diversi tra le due pagine</span>
                  </div>
                  <div className="border border-amber-200 rounded-md bg-white overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-50 border-b border-amber-200">
                        <tr className="text-xs uppercase tracking-wider text-amber-800">
                          <th className="text-left px-3 py-2">Titolo corso</th>
                          <th className="text-left px-3 py-2">Campo</th>
                          <th className="px-3 py-2 text-center">Valore p-learning</th>
                          <th className="px-3 py-2 text-center">Valore attuale italiacorsi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diff.modified.flatMap((m, i) =>
                          (m.diffs || []).map((df, k) => (
                            <tr key={`m${i}-${k}`} className="border-b border-slate-100">
                              {k === 0 ? (
                                <td rowSpan={(m.diffs || []).length} className="px-3 py-2 align-top text-slate-900 font-medium border-r border-slate-100">
                                  {m.plearning?.title}
                                </td>
                              ) : null}
                              <td className="px-3 py-2 text-xs uppercase tracking-wide text-slate-600">{df.label}</td>
                              <td className="px-3 py-2 text-center font-mono text-xs font-semibold text-emerald-700 bg-emerald-50/40">
                                {df.plearning}{df.field === "hours" ? " ore" : ""}
                              </td>
                              <td className="px-3 py-2 text-center font-mono text-xs text-red-700 bg-red-50/40">
                                {df.italia}{df.field === "hours" ? " ore" : ""}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
              <div className="text-xs text-slate-600">
                <span className="font-semibold">Anteprima renderizzata</span> — come apparirà sulla pagina prodotto italiacorsi.it
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-2" onClick={copyHTML} data-testid="preview-copy-btn">
                  <Copy className="w-3.5 h-3.5" /> Copia HTML
                </Button>
              </div>
            </div>
            <div className="p-0 overflow-hidden" data-testid="html-preview-container">
              <iframe
                srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>body{margin:0;padding:16px;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;background:#fff;color:#0f172a;}table.corsi{width:100%;border-collapse:collapse;}table.corsi th,table.corsi td{padding:8px;border:1px solid #e2e8f0;}</style></head><body>${previewHtml}</body></html>`}
                title="Anteprima HTML tabella corsi"
                className="w-full"
                style={{ height: "70vh", border: "none", display: "block" }}
                data-testid="html-preview-iframe"
              />
            </div>
          </div>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-slate-600 hover:text-slate-900 font-mono">Mostra codice HTML sorgente</summary>
            <pre className="mt-2 p-3 bg-slate-900 text-slate-200 text-[11px] rounded-md overflow-auto max-h-64 font-mono">
              {previewHtml}
            </pre>
          </details>
        </TabsContent>

        <TabsContent value="full" className="mt-4">
          <div className="border border-slate-200 rounded-md bg-white overflow-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b-2 border-slate-200 sticky top-0">
                <tr className="text-xs uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Titolo corso</th>
                  <th className="px-3 py-2 text-center">Durata</th>
                  <th className="px-3 py-2 text-center">CFP CNPI</th>
                  <th className="px-3 py-2 text-center">CFP CNAPPC</th>
                  <th className="px-3 py-2 text-center">CFP CNI</th>
                  <th className="px-3 py-2 text-center">CFP CNG</th>
                  <th className="px-3 py-2 text-center">CFP Geologi</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(byCat).sort().map((cat) => (
                  <>
                    <tr key={`h-${cat}`} className="bg-slate-100">
                      <td colSpan={7} className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-700">{cat}</td>
                    </tr>
                    {byCat[cat].map((c, i) => (
                      <tr key={`${cat}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-900">{c.title}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{c.hours || "-"} ore</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNPI || "-"}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNAPPC || "-"}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNI || "-"}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.CNG || "-"}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{c.cfp?.Geologi || "-"}</td>
                      </tr>
                    ))}
                  </>
                ))}
                {courses.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-500">Nessun corso. Esegui sync.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" /> Anteprima pubblicazione — {info.name}
            </DialogTitle>
            <DialogDescription>
              Vedi qui sotto come apparirà la pagina <b>{info.target_url.replace('https://', '')}</b> DOPO la pubblicazione. Nulla è stato ancora scritto sul sito.
            </DialogDescription>
          </DialogHeader>

          <div className="border border-amber-200 bg-amber-50 rounded-md p-3 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <b>Azione irreversibile:</b> alla conferma, la descrizione del prodotto WooCommerce verrà sostituita con la nuova tabella
              ({latest?.total_count || 0} corsi). Le categorie useranno i nomi originali di p-learning
              (es. "Edilizia, Urbanistica e Ambiente" invece di "EDILIZIA"). L'operazione viene tracciata in log.
            </div>
          </div>

          <div className="flex-1 border border-slate-200 rounded-md bg-slate-100 overflow-hidden relative min-h-[500px]">
            {loadingPreview && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-10">
                <div className="flex items-center gap-2 text-slate-700">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Rendering anteprima della pagina live…
                </div>
              </div>
            )}
            <iframe
              srcDoc={pagePreview}
              title="Anteprima pagina italiacorsi"
              className="w-full h-full min-h-[500px] bg-white"
              sandbox="allow-same-origin"
              data-testid="page-preview-iframe"
            />
          </div>

          <DialogFooter className="gap-2">
            <a href={info.target_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-slate-900 self-center mr-auto flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Apri pagina live attuale
            </a>
            <Button variant="outline" onClick={() => setPublishOpen(false)} disabled={publishing}>Annulla</Button>
            <Button onClick={publish} disabled={publishing || loadingPreview} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="confirm-publish-btn">
              <Upload className="w-4 h-4" /> {publishing ? "Pubblico…" : "Conferma e pubblica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setS(r.data)).catch(() => toast.error("Errore caricamento"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings`, s);
      toast.success("Impostazioni salvate. Scheduler aggiornato.");
    } catch { toast.error("Errore salvataggio"); }
    setSaving(false);
  };

  if (!s) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-2 mb-4">
          <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Impostazioni</h1>
        <p className="text-sm text-slate-600 mt-1">Configura la sincronizzazione giornaliera automatica.</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Sync automatico</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Sync automatico attivo</Label>
              <p className="text-xs text-slate-500">Se disattivato, l'aggiornamento avviene solo quando clicchi "Esegui sync ora".</p>
            </div>
            <Switch checked={s.auto_sync_enabled} onCheckedChange={(v) => setS({ ...s, auto_sync_enabled: v })} data-testid="settings-auto-sync-switch" />
          </div>
          <div className={`rounded-md bg-slate-50 border border-slate-200 p-3 text-sm ${!s.auto_sync_enabled ? "opacity-50" : ""}`} data-testid="settings-schedule-info">
            Controllo automatico <b>ogni giorno alle 03:00</b> <span className="font-mono text-xs">(Europe/Rome)</span>
          </div>
          <div className={`flex items-center justify-between ${!s.auto_sync_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <div>
              <Label className="text-sm">Pubblicazione automatica su italiacorsi.it</Label>
              <p className="text-xs text-slate-500">Se ci sono modifiche, le tabelle vengono pubblicate da sole (con backup). Se la scansione è incompleta non pubblica e ti avvisa via email.</p>
            </div>
            <Switch checked={s.auto_publish_enabled ?? true} onCheckedChange={(v) => setS({ ...s, auto_publish_enabled: v })} data-testid="settings-auto-publish-switch" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Notifiche email</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Invia resoconto via email</Label>
              <p className="text-xs text-slate-500">Ricevi un'email solo quando ci sono modifiche pubblicate o errori.</p>
            </div>
            <Switch checked={s.email_enabled} onCheckedChange={(v) => setS({ ...s, email_enabled: v })} data-testid="settings-email-switch" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Destinatario</Label>
            <Input type="email" value={s.email_recipient}
                   onChange={(e) => setS({ ...s, email_recipient: e.target.value })}
                   data-testid="settings-email-input" />
          </div>
          <p className="text-[11px] text-slate-500 italic">
            Le email vengono inviate tramite Gmail configurato sul server.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="settings-save-btn">
          <Check className="w-4 h-4" /> {saving ? "Salvo…" : "Salva impostazioni"}
        </Button>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<PinGate />} />
        <Route path="/*" element={
          <RequireAuth>
            <Shell>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/subscription/:key" element={<Detail />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Routes>
            </Shell>
          </RequireAuth>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
